import { DateTime } from 'luxon';
import { parseUTCDate } from './timezoneUtils';

// Summed in cents to avoid float drift.
export const sumBy = (legs, key) => legs.reduce((sum, l) => sum + Math.round(Number(l[key] || 0) * 100), 0) / 100;

const toMillis = (value) => parseUTCDate(value)?.toMillis() ?? NaN;

export const legsOf = (b) => (b.isGroup ? b.legs : [b]);

// A group's next session to start, or its last once all have started.
export const nextStart = (b, now = Date.now()) => {
  const starts = legsOf(b).map((l) => toMillis(l.startDateTime));
  return starts.find((t) => t > now) ?? starts[starts.length - 1];
};

// Collapses a group's rows into one booking-shaped item per status, since upcoming legs can be cancelled after others were played.
export const groupBookings = (bookings) => {
  const groups = new Map();
  const result = [];
  for (const b of bookings) {
    const key = `${b.bookingGroupID}:${b.bookingStatus}`;
    if (!b.bookingGroupID) {
      result.push(b);
    } else if (groups.has(key)) {
      groups.get(key).push(b);
    } else {
      const legs = [b];
      groups.set(key, legs);
      result.push(legs);
    }
  }
  return result.map((item) => {
    if (!Array.isArray(item)) return item;
    const legs = item.sort((a, b) => toMillis(a.startDateTime) - toMillis(b.startDateTime));
    return {
      ...legs[0],
      isGroup: true,
      legs,
      groupSize: legs.length,
      startDateTime: legs[0].startDateTime,
      endDateTime: legs[legs.length - 1].endDateTime,
      totalPrice: sumBy(legs, 'totalPrice'),
      refundAmount: sumBy(legs, 'refundAmount'),
    };
  });
};

// "Mar 3, Mar 10, Mar 17", or "6 sessions · Mar 3 – Apr 7" when longer.
export const summarizeLegs = (legs, tz) => {
  const spans = legs.map((l) => [parseUTCDate(l.startDateTime)?.setZone(tz), parseUTCDate(l.endDateTime)?.setZone(tz)]);
  const day = (dt) => dt?.toFormat('LLL d') ?? '—';
  const range = ([s, e]) => `${s?.toLocaleString(DateTime.TIME_SIMPLE)} – ${e?.toLocaleString(DateTime.TIME_SIMPLE)}`;
  const firstRange = range(spans[0]);
  return {
    dates: spans.length <= 3
      ? spans.map(([s]) => day(s)).join(', ')
      : `${spans.length} sessions · ${day(spans[0][0])} – ${day(spans[spans.length - 1][0])}`,
    times: spans.every((span) => range(span) === firstRange) ? firstRange : 'Times vary',
  };
};
